import { displayValue, MISSING_VALUE } from '../kit/displayValue';
import { isValue, type Maybe } from './geometry';

/**
 * Alternative textuelle d'un graphique : la phrase de synthese et les lignes de
 * la table masquee.
 *
 * Elles sont **derivees des memes valeurs** que le dessin, jamais ecrites a
 * part : une phrase tapee a la main finit par dire autre chose que la courbe.
 * Seuls des reperes exacts y figurent (premiere, derniere, plus haute, plus
 * basse valeur) — ni moyenne, ni tendance, ni arrondi : ce serait du calcul
 * metier, et un nombre que le serveur n'a pas rendu.
 */

export type ValueFormatter = (value: number) => string;

export const defaultFormat: ValueFormatter = (value) => displayValue(value);

/**
 * Valeur mise en forme, ou « — » quand elle manque : jamais `0`.
 *
 * `missing` remplace le tiret quand l'appelant sait **pourquoi** la valeur
 * manque et que le tiret en dirait moins (« < 10 » : sous le seuil d'affichage).
 */
export const formatMaybe = (value: Maybe, format: ValueFormatter, missing: string = MISSING_VALUE): string =>
  isValue(value) ? format(value) : missing;

const plural = (count: number, one: string, many: string): string => `${String(count)} ${count > 1 ? many : one}`;

/** Phrase d'une serie : nombre de valeurs, absences, derniere, plus haute, plus basse. */
export const describeSeries = (
  name: string,
  categories: readonly string[],
  values: readonly Maybe[],
  format: ValueFormatter,
): string => {
  const present = categories.flatMap((_, index) => (isValue(values[index]) ? [index] : []));
  const missing = categories.length - present.length;

  if (present.length === 0) {
    return `${name} : aucune valeur.`;
  }

  const at = (index: number): string => `${format(values[index]!)} (${categories[index] ?? ''})`;
  const last = present[present.length - 1]!;

  if (present.length === 1) {
    return `${name} : une seule valeur, ${at(last)}.`;
  }

  const byValue = [...present].sort((a, b) => values[a]! - values[b]!);
  const absent = missing > 0 ? `, ${plural(missing, 'absente', 'absentes')}` : '';

  return `${name} : ${plural(present.length, 'valeur', 'valeurs')}${absent} ; dernière ${at(last)}, plus haute ${at(byValue[byValue.length - 1]!)}, plus basse ${at(byValue[0]!)}.`;
};

/** Phrase de tete : combien de points, de quelle categorie a quelle categorie. */
export const describeRange = (categories: readonly string[]): string =>
  categories.length === 0
    ? 'Aucun point.'
    : categories.length === 1
      ? `Un seul point : ${categories[0] ?? ''}.`
      : `${String(categories.length)} points, de ${categories[0] ?? ''} à ${categories[categories.length - 1] ?? ''}.`;

export interface AlternativeRow {
  readonly category: string;
  readonly cells: readonly string[];
}

/** Lignes de la table : une par categorie, « — » pour chaque valeur absente. */
export const tableRows = (
  categories: readonly string[],
  series: readonly { readonly values: readonly Maybe[] }[],
  format: ValueFormatter,
  missing: string = MISSING_VALUE,
): AlternativeRow[] =>
  categories.map((category, index) => ({
    category,
    cells: series.map((one) => formatMaybe(one.values[index], format, missing)),
  }));
